import React, { useEffect, useState } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Wallet, Download, Search } from 'lucide-react';
import { exportToExcel, formatCashHandoversForExport } from '../utils/excelExport';

export default function CashHandover() {
  const { user } = useAuth();
  const [handovers, setHandovers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchBalances, setBranchBalances] = useState({});
  const [cashBalance, setCashBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [branchFilter, setBranchFilter] = useState('all');
  const [resetFormData, setResetFormData] = useState({
    branch_id: '',
    new_balance: 0
  });
  const [formData, setFormData] = useState({
    branch_id: '',
    amount: '',
    handed_to: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (!user) return;
    fetchHandovers();
    fetchCashBalance();
    if (user.role === 'admin') {
      fetchBranches();
      fetchAllBranchBalances();
    }
  }, [user?.id]);

  const fetchCashBalance = async () => {
    try {
      const branchId = user?.branch_id;
      if (!branchId) return;
      const response = await api.get(`/cash-balance?branch_id=${branchId}`);
      setCashBalance(response.data.current_balance);
    } catch (error) {
      toast.error('Failed to fetch cash balance');
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches');
      setBranches(response.data);
    } catch (error) {
      toast.error('Failed to fetch branches');
    }
  };

  const fetchAllBranchBalances = async () => {
    try {
      const response = await api.get('/cash-balance/all-branches');
      const balances = {};
      
      response.data.forEach(branch => {
        balances[branch.branch_id] = {
          branch_name: branch.branch_name,
          current_balance: branch.current_balance,
          total_cash_collected: branch.total_cash_collected,
          total_cash_expenses: branch.total_cash_expenses,
          total_handed_over: branch.total_handed_over
        };
      });
      
      setBranchBalances(balances);
    } catch (error) {
      console.error('Failed to fetch branch balances');
    }
  };

  const fetchHandovers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/cash-handovers');
      setHandovers(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      toast.error('Failed to fetch handovers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // For admin, branch_id is required
    if (user?.role === 'admin' && !formData.branch_id) {
      toast.error('Please select a branch');
      return;
    }
    
    // For manager/lead, use their branch_id
    const branch_id = user?.role === 'admin' ? formData.branch_id : user?.branch_id;
    
    try {
      await api.post('/cash-handovers', {
        ...formData,
        branch_id: branch_id,
        amount: parseFloat(formData.amount)
      });
      toast.success('Cash handover recorded successfully');
      setDialogOpen(false);
      setFormData({
        branch_id: '',
        amount: '',
        handed_to: '',
        date: new Date().toISOString().split('T')[0]
      });
      fetchHandovers();
      fetchCashBalance();
      if (user?.role === 'admin') {
        fetchAllBranchBalances();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record handover');
    }
  };

  const handleResetBalance = async () => {
    if (!resetFormData.branch_id) {
      toast.error('Please select a branch');
      return;
    }

    try {
      await api.post('/cash-handovers/reset-balance', null, {
        params: {
          branch_id: resetFormData.branch_id,
          new_balance: parseFloat(resetFormData.new_balance) || 0
        }
      });
      toast.success('Cash balance reset successfully');
      setResetDialogOpen(false);
      setResetFormData({ branch_id: '', new_balance: 0 });
      fetchHandovers();
      fetchCashBalance();
      if (user?.role === 'admin') {
        fetchAllBranchBalances();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset cash balance');
    }
  };

  const handleExport = () => {
    try {
      const formattedData = formatCashHandoversForExport(filteredHandovers);
      exportToExcel(formattedData, 'cash_handovers', 'Cash Handovers');
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const filteredHandovers = handovers.filter(handover => {
    if (branchFilter !== 'all' && handover.branch_id !== branchFilter) return false;
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      handover.handed_to.toLowerCase().includes(search) ||
      handover.date.includes(search) ||
      handover.created_by_username.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk' }} data-testid="cash-handover-title">
            Cash Handover
          </h1>
          <p className="text-slate-600 mt-1">Track cash handovers to admin</p>
        </div>
        <div className="flex gap-2">
          {user?.role === 'admin' && (
            <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2 border-red-200 text-red-600 hover:bg-red-50">
                  <Wallet size={16} />
                  Reset Balance
                </Button>
              </DialogTrigger>
            </Dialog>
          )}
          
          <Button onClick={handleExport} variant="outline" className="gap-2" disabled={filteredHandovers.length === 0}>
            <Download size={16} />
            Export to Excel
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-handover-btn" className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white gap-2">
                <Plus size={16} />
                Record Handover
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Record Cash Handover</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {user?.role === 'admin' && (
                <div className="space-y-2">
                  <Label htmlFor="branch_select">Select Branch *</Label>
                  <Select
                    value={formData.branch_id}
                    onValueChange={(value) => {
                      setFormData({ ...formData, branch_id: value });
                      // Update cash balance for selected branch
                      const selectedBranch = branchBalances[value];
                      if (selectedBranch) {
                        setCashBalance(selectedBranch.current_balance || 0);
                      }
                    }}
                  >
                    <SelectTrigger id="branch_select">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map(branch => (
                        <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {(user?.role !== 'admin' || formData.branch_id) && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <Label className="text-sm text-green-800">Current Cash Balance</Label>
                  <p className="text-2xl font-bold text-green-600">
                    ₹{(user?.role === 'admin' && formData.branch_id 
                      ? (branchBalances[formData.branch_id]?.current_balance || 0) 
                      : cashBalance || 0).toFixed(2)}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="amount">Amount to Handover *</Label>
                <Input
                  id="amount"
                  data-testid="handover-amount-input"
                  type="number"
                  step="0.01"
                  min="0"
                  max={user?.role === 'admin' && formData.branch_id 
                    ? (branchBalances[formData.branch_id]?.current_balance || 0) 
                    : cashBalance}
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  disabled={user?.role === 'admin' && !formData.branch_id}
                />
                {user?.role === 'admin' && !formData.branch_id && (
                  <p className="text-xs text-slate-500">Select a branch first</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="handed_to">Handed Over To *</Label>
                <Input
                  id="handed_to"
                  data-testid="handed-to-input"
                  value={formData.handed_to}
                  onChange={(e) => setFormData({ ...formData, handed_to: e.target.value })}
                  placeholder="Enter admin name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  data-testid="handover-date-input"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <Button data-testid="submit-handover-btn" type="submit" className="w-full">
                Record Handover
              </Button>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="border-green-200 bg-green-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-green-600 rounded-xl">
              <Wallet className="w-8 h-8 text-white" />
            </div>
            <div>
              <p className="text-sm text-green-800 font-medium">Current Cash Balance</p>
              <p className="text-3xl font-bold text-green-900" data-testid="current-cash-balance">₹{(cashBalance || 0).toFixed(2)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Branch-wise Cash Balances (Admin Only) */}
      {user?.role === 'admin' && Object.keys(branchBalances).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Branch-wise Cash Balances</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(branchBalances).map(([branchId, balance]) => (
                <div key={branchId} className="p-4 bg-slate-50 rounded-lg border">
                  <p className="text-sm font-medium text-slate-700">{balance?.branch_name || 'Unknown Branch'}</p>
                  <p className="text-2xl font-bold text-green-600 mt-2">₹{(balance?.current_balance || 0).toFixed(2)}</p>
                  <div className="grid grid-cols-3 gap-2 mt-2 text-xs text-slate-600">
                    <div>
                      <p className="text-slate-500">Collected</p>
                      <p className="font-medium">₹{(balance?.total_cash_collected || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Expenses</p>
                      <p className="font-medium text-red-600">₹{(balance?.total_cash_expenses || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Handed</p>
                      <p className="font-medium">₹{(balance?.total_handed_over || 0).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Handover History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                <Input
                  placeholder="Search by name, date..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            {user?.role === 'admin' && branches.length > 0 && (
              <div>
                <Label>Filter by Branch</Label>
                <Select value={branchFilter} onValueChange={(value) => setBranchFilter(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All branches" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All branches</SelectItem>
                    {branches.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-slate-600">Loading handovers...</p>
            </div>
          ) : filteredHandovers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No handovers {searchTerm ? 'found matching your search' : 'recorded yet'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredHandovers.map((handover) => (
                <div
                  key={handover.id}
                  data-testid={`handover-${handover.id}`}
                  className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">Handed to: {handover.handed_to}</h3>
                      <div className="flex gap-4 mt-2 text-sm text-slate-600">
                        <span>Date: {handover.date}</span>
                        <span>By: {handover.created_by_username || 'System'}</span>
                      </div>
                      {handover.balance_before != null && handover.balance_after != null && (
                        <div className="flex gap-4 mt-1 text-xs text-slate-500">
                          <span>Balance Before: ₹{handover.balance_before.toFixed(2)}</span>
                          <span>Balance After: ₹{handover.balance_after.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-red-600">-₹{(handover.amount || 0).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reset Balance Dialog */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Cash Balance</DialogTitle>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handleResetBalance(); }}>
            <div className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ This will reset the cash balance for the selected branch. Use this to clear old values and start fresh tracking.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reset-branch">Select Branch *</Label>
                <Select 
                  value={resetFormData.branch_id} 
                  onValueChange={(value) => setResetFormData({ ...resetFormData, branch_id: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name} (Current: ₹{(branchBalances[branch.id]?.current_balance || 0).toFixed(2)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reset-balance">New Balance Amount</Label>
                <Input
                  id="reset-balance"
                  type="number"
                  step="0.01"
                  value={resetFormData.new_balance}
                  onChange={(e) => setResetFormData({ ...resetFormData, new_balance: e.target.value })}
                  placeholder="Enter 0 or any starting balance"
                />
                <p className="text-xs text-slate-500">Leave as 0 to reset to zero, or enter a new starting balance</p>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setResetDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" variant="destructive" className="flex-1">
                  Reset Balance
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
